import axios from "axios";
import dictionary from "../dictionary";
import functions from "../functions";
import { getCompanyId } from "../company";
// The one session both halves of this system share.
import hostFunctions from "../../functions";

const WHITELIST = [
  "login",
  "system/user/forgot_password",
  "password/reset",
  "user/account/activate",
  // Redeemed from an emailed link, so the caller has no session yet.
  "user/invitation/accept",
  "user/invitation/details",
  "user/invitation/complete",
];

const apiCall = async (endpoint, data = {}) => {
  const isWhitelisted = WHITELIST.includes(endpoint);

  // ✅ CORRECTED: Check login state using the new isLoggedIn() method
  // This checks for the existence of the user data cookie (set by frontend)
  // OR the JWT token (if readable and valid).
  if (!isWhitelisted && !functions.isLoggedIn()) {
    // Nobody is signed in, so send them to the login page rather than leaving
    // an accounting page sitting there failing every request behind it.
    hostFunctions.endSession();
    return {
      status: "auth", // Clear distinction: Authentication required
      message: "Your session has ended. Please sign in again.",
      details: false,
    };
  }

  try {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    // 👇 Only add the Authorization header if the token is readable.
    // If the token is HttpOnly, this will be null, but the cookie will
    // still be sent automatically via withCredentials: true.
    const token = functions.getToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    // Who is posting this, and whose books into. The backend has no session of
    // its own — like every other endpoint in this system, the caller names
    // itself in the body — and the company decides which set of books the
    // request opens. Both are attached here so no individual page has to
    // remember them, and a page that forgets cannot end up writing into the
    // wrong company.
    const actor = functions.getUserData();
    const companyId = getCompanyId();
    const isPlainBody = data && typeof data === "object" && !Array.isArray(data);

    const body = isPlainBody
      ? {
          ...(actor?.user_id ? { user_id: actor.user_id } : {}),
          ...(companyId ? { company_id: companyId } : {}),
          ...data,
        }
      : data;

    // No credentials. These pages came from a system that authenticated with a
    // cookie; here the session is a token in localStorage, sent in the header
    // above, so there is nothing for the browser to attach. Asking for them
    // anyway broke every accounting request: the API answers with
    // "Access-Control-Allow-Origin: *", and a browser refuses a credentialed
    // response that does not name its origin exactly — the preflight passed and
    // the POST itself was blocked.
    const response = await axios.post(dictionary.apiHost + endpoint, body, {
      headers,
    });

    return response.data;
  } catch (error) {
    // ✅ Detailed error handling - separates network vs. server errors
    if (error.response) {
      // The server responded with a status outside 2xx
      if (error.response.status === 401) {
        // Unauthorized – clear local session
        functions.clearAuth();
        hostFunctions.endSession();
        return {
          status: "auth",
          message:
            error.response.data?.message ||
            "Session expired. Please login again.",
          details: error.response.data || false,
        };
      }
      return {
        status: "error",
        message: error.response.data?.message || "Request failed",
        details: error.response.data || false,
      };
    } else if (error.request) {
      // The request was made but no response received (network down)
      return {
        status: "conn",
        message: "Could not reach the server. Check your connection.",
        details: false,
      };
    } else {
      // Something else happened (e.g., request config error)
      return {
        status: "error",
        message: error.message || "An unexpected error occurred.",
        details: false,
      };
    }
  }
};

export default apiCall;
